import { useState, useEffect } from "react";
import { useParams } from "react-router";
import React from "react";
import API from "../helpers/api";
import { optimizeImageUrl } from "../helpers/imageOptimization";

const ATTENDANCE_LABELS = {
  hadir: "Hadir",
  surat_sakit: "Surat Sakit",
  sakit: "Sakit",
  izin: "Izin",
  alpha: "Alpha",
};

const ATTENDANCE_DEDUCTIONS = {
  hadir: 0,
  surat_sakit: 0,
  sakit: 3,
  izin: 3,
  alpha: 10,
};

const normalizeAttendanceStatus = (status) => {
  if (status === true || status === 1 || status === "1") return "hadir";
  if (status === false || status === 0 || status === "0") return "alpha";

  const normalized = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (normalized === "true" || normalized === "hadir") return "hadir";
  if (normalized === "false" || normalized === "tidak_hadir") return "alpha";
  if (ATTENDANCE_LABELS[normalized]) return normalized;
  return "";
};

const getAttendanceStatusLabel = (status) =>
  ATTENDANCE_LABELS[normalizeAttendanceStatus(status)] || "-";

const calculateCommitmentScore = (dataAbsensi) => {
  const deduction = dataAbsensi.reduce((total, item) => {
    const status = normalizeAttendanceStatus(item.status);
    return total + (ATTENDANCE_DEDUCTIONS[status] || 0);
  }, 0);

  return Math.max(0, 100 - deduction);
};

const normalizeAbsensi = (payload) => {
  const source = payload?.data ?? payload ?? {};
  const dataAbsensi =
    [
      source.dataAbsensi,
      source.absensi,
      source.kehadiran,
      payload?.dataAbsensi,
      Array.isArray(source) ? source : null,
    ].find(Array.isArray) || [];
  const persentaseKehadiran =
    source.persentaseKehadiran ??
    source.persentase_kehadiran ??
    payload?.persentaseKehadiran;

  return {
    ...source,
    dataAbsensi,
    persentaseKehadiran:
      persentaseKehadiran ??
      (dataAbsensi.length > 0 ? calculateCommitmentScore(dataAbsensi) : 0),
  };
};

const DetailRapor = () => {
  const [raport, setRaport] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [kehadiran, setKehadiran] = useState({});
  const { turn, nim } = useParams();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const raporRes = await API.get(`/users/${nim}/rapor/${turn}`);
        setRaport(raporRes.data.data || {});

        try {
          const absensiRes = await API.get(`/users/${nim}/absensi/${turn}`);
          setKehadiran(normalizeAbsensi(absensiRes.data));
        } catch (_error) {
          setKehadiran(normalizeAbsensi({}));
        }
      } catch (_error) {
        setRaport({});
        setKehadiran(normalizeAbsensi({}));
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [nim, turn]);

  const getProdi = (nim) => {
    switch (nim.slice(6, 8)) {
      case "20":
        return "Teknik Informatika";
      case "30":
        return "Teknik Komputer";
      case "40":
        return "Sistem Informasi";
      case "60":
        return "Pendidikan Teknologi Informasi";
      case "70":
        return "Teknologi Informasi";
      default:
        return "-";
    }
  };

  const determineGrade = (average) => {
    if (average > 80) return "A";
    if (average > 65) return "B";
    if (average > 0) return "C";
    return "0";
  };

  const user = raport.user || {};
  const showFeedbackCLevel =
    Number(user.jabatan?.id_jabatan ?? user.id_jabatan ?? raport.id_jabatan) === 9 ||
    String(user.jabatan?.jabatan ?? user.jabatan ?? "")
      .trim()
      .toLowerCase() === "ire";
  const userProker = Array.isArray(user.proker) ? user.proker : [];
  const dataAbsensi = Array.isArray(kehadiran.dataAbsensi)
    ? kehadiran.dataAbsensi
    : [];
  const groupedDetail = (
    Array.isArray(raport.detail) ? raport.detail : []
  ).reduce((groups, item) => {
    const idAspek = item.sub_aspek?.id_aspek || "unknown";
    const aspek = item.sub_aspek?.aspek?.aspek || "-";
    const existing = groups.find((group) => group.id_aspek === idAspek);
    const subAspek = {
      id_subaspek: item.id_subaspek,
      sub_aspek: item.sub_aspek?.sub_aspek || "-",
      deskripsi: item.sub_aspek?.deskripsi || "-",
      nilai: item.nilai,
    };

    if (existing) {
      existing.sub_aspek.push(subAspek);
      return groups;
    }

    return [
      ...groups,
      {
        id_aspek: idAspek,
        aspek,
        sub_aspek: [subAspek],
      },
    ];
  }, []);
  const totalDetailRows = groupedDetail.reduce(
    (total, item) => total + Math.max(item.sub_aspek.length, 1),
    0,
  );

  return (
    <>
      <section className="bg-background">
        <div className="mx-4 md:mx-12 py-28">
          <img
            src="/assets/logo.png"
            className="h-48 mx-auto mb-24"
            alt="logo"
          />
          <h1 className="text-5xl md:text-7xl text-sgeOcean font-semibold tracking-widest text-center">
            R A P O R
          </h1>

          {/* Biodata Header */}
          <div className="flex justify-center mt-28 mb-0">
            <div className="bg-primary text-white font-semibold text-2xl md:text-3xl py-4 px-10 md:px-16 tracking-widest rounded-t-xl shadow-lg">
              BIODATA
            </div>
          </div>

          {/* Biodata Card */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="flex flex-col md:flex-row">
                {/* Photo Section */}
                <div
                  className={`bg-gradient-to-br from-primary-light to-primary flex-shrink-0 w-full md:w-64 lg:w-72 min-h-[280px] flex items-center justify-center p-6 ${
                    isLoading ? " animate-pulse " : ""
                  }`}
                >
                  {!isLoading ? (
                    user.foto !== null && user.foto !== "" && user.foto !== "-" ? (
                      <img
                        className="h-64 w-auto max-w-full object-cover rounded-xl shadow-md"
                        src={optimizeImageUrl(user.foto, 400) || ""}
                        alt="Foto"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-white text-center gap-3">
                        <div className="w-24 h-24 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-white opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <p className="text-sm opacity-80 font-medium">Foto belum tersedia</p>
                      </div>
                    )
                  ) : (
                    <div className="flex flex-col items-center justify-center text-white text-center gap-3">
                      <div className="w-24 h-24 rounded-full bg-white bg-opacity-20 animate-pulse"></div>
                      <div className="w-32 h-4 rounded bg-white bg-opacity-20 animate-pulse"></div>
                    </div>
                  )}
                </div>

                {/* Info Section */}
                <div className="flex-1 p-6 md:p-8">
                  {isLoading ? (
                    <div className="space-y-5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex gap-4 items-center">
                          <div className="animate-pulse bg-gray-200 rounded-lg h-5 w-44"></div>
                          <div className="animate-pulse bg-gray-100 rounded-lg h-5 flex-1"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: "0 8px" }}>
                      <tbody>
                        {/* Nama */}
                        <tr>
                          <td className="text-primary font-semibold text-sm uppercase tracking-wide align-top pr-2 whitespace-nowrap" style={{ width: "180px" }}>Nama</td>
                          <td className="text-primary font-semibold align-top px-2" style={{ width: "16px" }}>:</td>
                          <td className="text-text-heading capitalize font-medium">{(user.nama || "-").toLowerCase()}</td>
                        </tr>
                        {/* Program Studi */}
                        <tr>
                          <td className="text-primary font-semibold text-sm uppercase tracking-wide align-top pr-2 whitespace-nowrap" style={{ width: "180px" }}>Program Studi</td>
                          <td className="text-primary font-semibold align-top px-2" style={{ width: "16px" }}>:</td>
                          <td className="text-text-heading font-medium">{getProdi(raport.nim + "")}</td>
                        </tr>
                        {/* Kementerian */}
                        <tr>
                          <td className="text-primary font-semibold text-sm uppercase tracking-wide align-top pr-2 whitespace-nowrap" style={{ width: "180px" }}>Kementerian / Kebiroan</td>
                          <td className="text-primary font-semibold align-top px-2" style={{ width: "16px" }}>:</td>
                          <td className="text-text-heading font-medium">{user.kementerian?.singkatan || "-"}</td>
                        </tr>
                        {/* Proker */}
                        {userProker.length !== 0 && (
                          <tr>
                            <td className="text-primary font-semibold text-sm uppercase tracking-wide align-top pr-2 whitespace-nowrap" style={{ width: "180px" }}>Proker</td>
                            <td className="text-primary font-semibold align-top px-2" style={{ width: "16px" }}>:</td>
                            <td className="text-text-heading font-medium">
                              <ul className="list-decimal list-inside space-y-1">
                                {userProker.map((item, index) => (
                                  <li key={index}>{item.proker || "-"}</li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                        {/* Hobi Selama Rapat */}
                        <tr>
                          <td className="text-primary font-semibold text-sm uppercase tracking-wide align-top pr-2 whitespace-nowrap" style={{ width: "180px" }}>Hobi Selama Rapat</td>
                          <td className="text-primary font-semibold align-top px-2" style={{ width: "16px" }}>:</td>
                          <td className="text-text-heading font-medium">{raport.hobi || "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-primary">
        <div className="relative flex flex-row content-center justify-evenly pt-28 pb-20">
          <div className="self-center text-center md:text-left py-auto">
            <h1 className="text-3xl text-white font-semibold tracking-widest text-center">
              K E H A D I R A N
            </h1>
          </div>
        </div>
        <div className="relative flex flex-col overflow-x-auto mx-4 md:mx-36 lg:mx-60 justify-center pb-24">
          <table className="table-auto text-white min-w-[600px]">
            <thead>
              <tr>
                <th className="border-solid border-2 border-white text-center p-3 md:p-6 min-w-[100px]">
                  KEGIATAN
                </th>
                <th className="border-solid border-2 border-white text-center p-3 md:p-6">
                  STATUS
                </th>
                <th className="border-solid border-2 border-white text-center p-3 md:p-6">
                  TOTAL
                </th>
                <th className="border-solid border-2 border-white text-center p-3 md:p-6 min-w-[200px] w-[35%]">
                  KETERANGAN
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <tr>
                    <td className="border-solid border-2 border-white text-center p-6">
                      <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                    </td>
                    <td className="border-solid border-2 border-white text-center p-6">
                      <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                    </td>
                    <td
                      rowSpan="100"
                      className="border-solid border-2 border-white text-center p-6"
                    >
                      <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                    </td>
                    <td
                      rowSpan="100"
                      className="border-solid border-2 border-white text-center p-6"
                    >
                      <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                    </td>
                  </tr>
                  <tr>
                    <td className="border-solid border-2 border-white text-center p-6">
                      <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                    </td>
                    <td className="border-solid border-2 border-white text-center p-6">
                      <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                    </td>
                  </tr>
                  <tr>
                    <td className="border-solid border-2 border-white text-center p-6">
                      <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                    </td>
                    <td className="border-solid border-2 border-white text-center p-6">
                      <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                    </td>
                  </tr>
                </>
              ) : dataAbsensi.length > 0 ? (
                dataAbsensi.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td className="border-solid border-2 border-white text-center p-3 md:p-6">
                        <div>
                          {item.kegiatan?.kegiatan ||
                            item.kegiatan ||
                            item.nama_kegiatan ||
                            item.nama ||
                            "-"}
                        </div>
                      </td>
                      <td className="border-solid border-2 border-white text-center p-3 md:p-6">
                        <div>{getAttendanceStatusLabel(item.status)}</div>
                      </td>
                      {index === 0 ? (
                        <td
                          rowSpan="100"
                          className="border-solid border-2 border-white text-center p-3 md:p-6"
                        >
                          <div>{kehadiran.persentaseKehadiran}%</div>
                        </td>
                      ) : (
                        <></>
                      )}
                      {index === 0 ? (
                        <td
                          rowSpan="100"
                          className="border-solid border-2 border-white p-3 md:p-6 align-top whitespace-pre-line leading-relaxed text-left min-w-[200px] w-[35%]"
                        >
                          <div>{raport.keterangan_absen || "-"}</div>
                        </td>
                      ) : (
                        <></>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="border-solid border-2 border-white text-center p-6"
                  >
                    Data absensi tidak tersedia
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <section className="bg-background">
        <div className="flex text-white font-semibold text-3xl pt-28 pb-20 mx-12 justify-center tracking-widest">
          <div className="text-center bg-primary p-6">P E N I L A I A N</div>
        </div>
        <div className="relative flex flex-col overflow-x-auto mx-4 md:mx-12 justify-center pb-24">
          <table className="table-auto text-black min-w-[900px]">
            <thead>
              <tr>
                <th className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6">
                  ASPEK
                </th>
                <th className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6">
                  SUB ASPEK
                </th>
                <th className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6 w-[7%]">
                  NILAI
                </th>
                <th className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6 w-[7%]">
                  TOTAL
                </th>
                <th className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6 min-w-[250px] w-[35%]">
                  Activity Reflection
                </th>
                {showFeedbackCLevel && (
                  <th className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6 min-w-[250px] w-[30%]">
                    Feedback C-Level
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  {[0, 1, 2].map((row) => (
                    <tr key={row}>
                      {Array.from({
                        length: showFeedbackCLevel ? 6 : 5,
                      }).map((_, column) => (
                        <td
                          key={column}
                          className="border-solid border-2 border-sgeTeal text-center p-6"
                        >
                          <div className="animate-pulse bg-info opacity-10 rounded h-6"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              ) : (
                <>
                  {groupedDetail.length > 0 ? (
                    groupedDetail.map((item, index) => {
                      const totalNilai = item.sub_aspek.reduce(
                        (acc, subItem) => acc + Number(subItem.nilai || 0),
                        0,
                      );
                      const average = totalNilai / item.sub_aspek.length;
                      const grade = determineGrade(average);
                      const firstSubAspek = item.sub_aspek[0];

                      return (
                        <React.Fragment key={item.id_aspek || index}>
                          <tr>
                            <td
                              className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6"
                              rowSpan={item.sub_aspek.length}
                            >
                              {item.aspek}
                            </td>
                            <td className="border-solid border-2 border-sgeTeal p-3 md:p-6">
                              <div className="font-semibold">
                                {firstSubAspek.sub_aspek}
                              </div>
                              <div className="font-normal text-sm mt-2">
                                {firstSubAspek.deskripsi}
                              </div>
                            </td>
                            <td className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6">
                              {firstSubAspek.nilai ?? "Nilai tidak tersedia"}
                            </td>
                            <td
                              className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6"
                              rowSpan={item.sub_aspek.length}
                            >
                              {grade}
                            </td>
                            {index === 0 ? (
                              <td
                                className="border-solid border-2 border-sgeTeal p-3 md:p-6 align-top whitespace-pre-line leading-relaxed text-left min-w-[250px] w-[35%]"
                                rowSpan={totalDetailRows}
                              >
                                {raport.motivasi || "-"}
                              </td>
                            ) : (
                              <></>
                            )}
                            {showFeedbackCLevel && index === 0 ? (
                              <td
                                className="border-solid border-2 border-sgeTeal p-3 md:p-6 align-top whitespace-pre-line leading-relaxed text-left min-w-[250px] w-[30%]"
                                rowSpan={totalDetailRows}
                              >
                                {raport.feedback_c_level || "-"}
                              </td>
                            ) : (
                              <></>
                            )}
                          </tr>
                          {item.sub_aspek.slice(1).map((subItem) => (
                            <tr key={subItem.id_subaspek}>
                              <td className="border-solid border-2 border-sgeTeal p-3 md:p-6">
                                <div className="font-semibold">
                                  {subItem.sub_aspek}
                                </div>
                                <div className="font-normal text-sm mt-2">
                                  {subItem.deskripsi}
                                </div>
                              </td>
                              <td className="border-solid border-2 border-sgeTeal text-center p-3 md:p-6">
                                {subItem.nilai ?? "Nilai tidak tersedia"}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={showFeedbackCLevel ? 6 : 5}
                        className="border-solid border-2 border-sgeTeal text-center p-6"
                      >
                        Nilai tidak tersedia
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
            <tbody></tbody>
          </table>
        </div>
      </section>
    </>
  );
};
export default DetailRapor;
